/**
 * Admin Front Facing Tracer Page
 * Tool for tracing front-facing building render polygons
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { MapCanvas } from '@/components/admin/map-tracer/MapCanvas';
import { TracingControls } from '@/components/admin/map-tracer/TracingControls';
import { CoordinatePanel } from '@/components/admin/map-tracer/CoordinatePanel';
import { Trash2, CheckCircle2, AlertTriangle } from 'lucide-react';
import {
  createFrontFacingPolygon,
  updateFrontFacingPolygon,
  getFrontFacingPolygons,
  getFrontFacingPolygonByUnitId,
  deleteFrontFacingPolygon,
  type FrontFacingPolygon,
} from '@/lib/admin/front-facing-coordinates';
import { getAllUnitsForAdmin } from '@/lib/admin/units';
import type { MapCoordinates } from '@/types/map';
import { Save, Loader2, Building2 } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ExteriorRenderUpload } from '@/components/admin/ExteriorRenderUpload';
import { getExteriorRenderUrl } from '@/lib/admin/site-images';
import { getExteriorRenderConfig, type BlockBViewId, type ImageSection } from '@/lib/front-facing-coordinates';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface PolygonHistory {
  points: number[][];
}

export default function AdminFrontFacingTracer() {
  const { toast } = useToast();
  const [selectedBlock, setSelectedBlock] = useState<'A' | 'B'>('A');
  const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [selectedPolygon, setSelectedPolygon] = useState<FrontFacingPolygon | null>(null);
  const [frontFacingPolygons, setFrontFacingPolygons] = useState<FrontFacingPolygon[]>([]);
  
  const [currentPolygon, setCurrentPolygon] = useState<number[][]>([]);
  const [history, setHistory] = useState<PolygonHistory[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [existingPolygons, setExistingPolygons] = useState<
    Map<string, { points: number[][]; color?: string }>
  >(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [showGrid, setShowGrid] = useState(false);
  const [hiddenPolygons, setHiddenPolygons] = useState<Set<string>>(new Set());
  const [allUnits, setAllUnits] = useState<Array<{ id: string; unit_number: string; block: 'A' | 'B' }>>([]);
  const [renderUrlA, setRenderUrlA] = useState<string | null>(null);
  const [renderUrlB, setRenderUrlB] = useState<string | null>(null);
  const [blockBRenderConfig, setBlockBRenderConfig] = useState<Awaited<ReturnType<typeof getExteriorRenderConfig>>>(null);
  const [selectedView, setSelectedView] = useState<BlockBViewId>('center');
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [imageLoadError, setImageLoadError] = useState(false);

  const polygonStartRef = useRef<number[][] | null>(null);

  // Load units and polygons on mount
  useEffect(() => {
    loadAllUnits();
    loadFrontFacingPolygons();
    loadRenderUrls();
  }, []);

  // Load render URLs
  const loadRenderUrls = async () => {
    try {
      const [urlA, urlB] = await Promise.all([
        getExteriorRenderUrl('A'),
        getExteriorRenderUrl('B'),
      ]);
      setRenderUrlA(urlA);
      setRenderUrlB(urlB);
    } catch (error) {
      console.error('Error loading render URLs:', error);
    }
  };

  // Load Block B multi-image config when Block B is selected
  useEffect(() => {
    if (selectedBlock !== 'B') {
      setBlockBRenderConfig(null);
      return;
    }
    let cancelled = false;
    getExteriorRenderConfig('B').then((config) => {
      if (!cancelled) setBlockBRenderConfig(config);
    });
    return () => { cancelled = true; };
  }, [selectedBlock]);

  // Refresh render URL when block changes
  const handleRenderUpdated = async (url: string) => {
    if (selectedBlock === 'A') {
      setRenderUrlA(url);
    } else {
      setRenderUrlB(url);
      const config = await getExteriorRenderConfig('B');
      setBlockBRenderConfig(config);
    }
    // Reload to get fresh URL
    await loadRenderUrls();
    // Reset image dimensions to trigger re-detection
    setImageDimensions(null);
    setImageLoadError(false);
  };

  // Current section for Block B multi-image (tabbed view)
  const blockBMultiImage = blockBRenderConfig?.type === 'multi-image';
  const currentSection: ImageSection | null = blockBMultiImage
    ? (blockBRenderConfig.sections.find((s) => s.id === selectedView) ?? null)
    : null;

  // Image URL for canvas: Block A or single B = config URL; Block B multi-image = selected view URL
  const imageUrl =
    selectedBlock === 'A'
      ? renderUrlA
      : blockBMultiImage && currentSection?.url
        ? currentSection.url
        : renderUrlB;

  // Detect image dimensions when image URL changes
  useEffect(() => {
    if (!imageUrl) {
      setImageDimensions(null);
      setImageLoadError(false);
      return;
    }

    setImageDimensions(null);
    setImageLoadError(false);

    const img = new Image();
    img.onload = () => {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      setImageLoadError(false);
    };
    img.onerror = () => {
      setImageLoadError(true);
      setImageDimensions(null);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Load polygon when unit, block, or view tab changes
  useEffect(() => {
    if (selectedUnitId) {
      loadPolygonForUnit(selectedUnitId);
    } else {
      setCurrentPolygon([]);
      setHistory([]);
      setHistoryIndex(-1);
    }
  }, [selectedUnitId, selectedBlock, selectedView, blockBMultiImage]);

  // Load front-facing polygons when block changes
  useEffect(() => {
    loadFrontFacingPolygons();
  }, [selectedBlock]);

  const loadAllUnits = async () => {
    try {
      const { data } = await getAllUnitsForAdmin();
      if (data) {
        // Get unique units (one per unit_number) for the selector
        const uniqueUnits = new Map<string, { id: string; unit_number: string; block: 'A' | 'B' }>();
        data.forEach((unit) => {
          if (!uniqueUnits.has(unit.unit_number)) {
            uniqueUnits.set(unit.unit_number, {
              id: unit.id,
              unit_number: unit.unit_number,
              block: unit.block,
            });
          }
        });
        setAllUnits(Array.from(uniqueUnits.values()));
      }
    } catch (error) {
      console.error('Error loading units:', error);
    }
  };

  const loadFrontFacingPolygons = async () => {
    try {
      const { data, error } = await getFrontFacingPolygons(selectedBlock);
      if (error) {
        console.error('Error loading front-facing polygons:', error);
        return;
      }
      
      if (data) {
        setFrontFacingPolygons(data);
        
        // Update existing polygons map
        const polygonsMap = new Map<string, { points: number[][]; color?: string }>();
        data.forEach((polygon) => {
          if (
            polygon.map_coordinates?.type === 'Polygon' &&
            polygon.map_coordinates.points &&
            Array.isArray(polygon.map_coordinates.points) &&
            polygon.map_coordinates.points.length >= 3
          ) {
            const key = polygon.unit_number;
            polygonsMap.set(key, {
              points: polygon.map_coordinates.points,
              color: 'rgba(59, 130, 246, 0.2)',
            });
          }
        });
        
        setExistingPolygons(polygonsMap);
      }
    } catch (error) {
      console.error('Error loading front-facing polygons:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPolygonForUnit = async (unitId: string) => {
    try {
      const viewTypeToLoad = blockBMultiImage ? `front-${selectedView}` : 'front';
      const { data, error } = await getFrontFacingPolygonByUnitId(unitId, viewTypeToLoad);
      if (error) {
        console.error('Error loading polygon for unit:', error);
        return;
      }
      
      if (data) {
        setSelectedPolygon(data);
        const coords = data.map_coordinates;
        if (coords?.type === 'Polygon' && coords.points && coords.points.length >= 3) {
          setCurrentPolygon(coords.points);
          setHistory([{ points: [...coords.points] }]);
          setHistoryIndex(0);
        } else {
          setCurrentPolygon([]);
          setHistory([]);
          setHistoryIndex(-1);
        }
      } else {
        // No polygon exists yet
        setSelectedPolygon(null);
        setCurrentPolygon([]);
        setHistory([]);
        setHistoryIndex(-1);
      }
    } catch (error) {
      console.error('Error loading polygon for unit:', error);
    }
  };

  const handlePointAdd = useCallback(
    (point: [number, number], insertIndex?: number) => {
      console.log('AdminFrontFacingTracer: handlePointAdd called', {
        point,
        insertIndex,
        selectedUnitId,
        selectedUnit,
        currentPolygonLength: currentPolygon.length,
      });
      
      if (!selectedUnitId) {
        console.warn('AdminFrontFacingTracer: Cannot add point - no unit selected', {
          selectedUnitId,
          selectedUnit,
        });
        toast({
          title: 'No Unit Selected',
          description: 'Please select a unit first',
          variant: 'destructive',
        });
        return;
      }

      if (!point || point.length !== 2 || isNaN(point[0]) || isNaN(point[1])) {
        console.error('Invalid point received:', point);
        toast({
          title: 'Invalid Point',
          description: 'Could not add point - invalid coordinates',
          variant: 'destructive',
        });
        return;
      }

      // Insert at specific index if provided, otherwise add to end
      const newPolygon = insertIndex !== undefined && insertIndex >= 0 && insertIndex <= currentPolygon.length
        ? [...currentPolygon.slice(0, insertIndex), point, ...currentPolygon.slice(insertIndex)]
        : [...currentPolygon, point];
      
      console.log('AdminFrontFacingTracer: Adding point', {
        point,
        selectedUnit,
        selectedUnitId,
        currentPolygonLength: currentPolygon.length,
        newPolygonLength: newPolygon.length,
        currentPolygonKey: selectedUnit || null,
      });
      
      setCurrentPolygon(newPolygon);

      // Update history
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push({ points: [...newPolygon] });
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    },
    [selectedUnitId, currentPolygon, history, historyIndex, toast]
  );

  const handlePointDelete = useCallback(
    (index: number) => {
      if (!selectedUnitId) return;
      if (currentPolygon.length <= 3) {
        toast({
          title: 'Cannot Delete',
          description: 'A polygon needs at least 3 points',
          variant: 'destructive',
        });
        return;
      }

      const newPolygon = currentPolygon.filter((_, i) => i !== index);
      setCurrentPolygon(newPolygon);

      // Update history
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push({ points: [...newPolygon] });
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    },
    [selectedUnitId, currentPolygon, history, historyIndex, toast]
  );

  const handlePointMove = useCallback(
    (index: number, point: [number, number]) => {
      if (!selectedUnitId) return;
      if (currentPolygon.length === 0 || index < 0 || index >= currentPolygon.length) return;

      const newPolygon = [...currentPolygon];
      newPolygon[index] = point;
      setCurrentPolygon(newPolygon);

      // Update history
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push({ points: [...newPolygon] });
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    },
    [selectedUnitId, currentPolygon, history, historyIndex]
  );

  const handlePolygonMoveStart = useCallback(() => {
    polygonStartRef.current = currentPolygon.map((p) => [p[0], p[1]]);
  }, [currentPolygon]);

  const handlePolygonMove = useCallback(
    (deltaX: number, deltaY: number) => {
      if (!selectedUnitId || currentPolygon.length === 0) return;

      // Store initial polygon on first move if not already stored
      if (!polygonStartRef.current) {
        polygonStartRef.current = currentPolygon.map((p) => [p[0], p[1]]);
      }

      // Apply delta to initial polygon position
      const newPolygon = polygonStartRef.current.map((point) => [
        point[0] + deltaX,
        point[1] + deltaY,
      ]) as number[][];
      setCurrentPolygon(newPolygon);

      // Update history
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push({ points: [...newPolygon] });
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    },
    [selectedUnitId, currentPolygon, history, historyIndex]
  );

  // Reset polygon start ref when polygon changes
  useEffect(() => {
    polygonStartRef.current = null;
  }, [selectedUnitId, selectedPolygon]);

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setCurrentPolygon([...history[newIndex].points]);
    }
  }, [history, historyIndex]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setCurrentPolygon([...history[newIndex].points]);
    }
  }, [history, historyIndex]);

  const handleClear = useCallback(() => {
    setCurrentPolygon([]);
    setHistory([]);
    setHistoryIndex(-1);
  }, []);

  const handleSave = async () => {
    if (currentPolygon.length < 3) {
      toast({
        title: 'Invalid Polygon',
        description: 'A polygon needs at least 3 points',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedUnitId) {
      toast({
        title: 'No Unit Selected',
        description: 'Please select a unit first',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      // For tabbed views: save coordinates as view-local (no transformation)
      const coordinates: MapCoordinates = {
        type: 'Polygon',
        points: currentPolygon,
      };

      const viewTypeToSave = blockBMultiImage ? `front-${selectedView}` : 'front';

      let result;
      if (selectedPolygon) {
        result = await updateFrontFacingPolygon(selectedPolygon.id, coordinates);
      } else {
        result = await createFrontFacingPolygon(selectedUnitId, coordinates, viewTypeToSave);
      }

      if (result.success && result.data) {
        // Reload polygons
        await loadFrontFacingPolygons();
        
        // Update selected polygon so it remains editable
        setSelectedPolygon(result.data);
        
        // Update current polygon to match saved data (no transformation for tabbed views)
        const coords = result.data.map_coordinates;
        if (coords?.type === 'Polygon' && coords.points && coords.points.length >= 3) {
          setCurrentPolygon(coords.points);
          setHistory([{ points: [...coords.points] }]);
          setHistoryIndex(0);
        }
        
        toast({
          title: 'Saved',
          description: `Front-facing polygon for ${selectedUnit} saved`,
        });
      } else {
        toast({
          title: 'Save Failed',
          description: result.error || 'Unknown error',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error saving polygon:', error);
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Get units for selected block
  const blockUnits = allUnits.filter(u => u.block === selectedBlock).sort((a, b) => {
    const numA = parseInt(a.unit_number.split('-')[1]);
    const numB = parseInt(b.unit_number.split('-')[1]);
    return numA - numB;
  });

  // For Block B multi-image: show only polygons for the current view tab
  const currentViewType = blockBMultiImage ? `front-${selectedView}` : 'front';
  const existingPolygonsForCanvas =
    blockBMultiImage
      ? (() => {
          const map = new Map<string, { points: number[][]; color?: string }>();
          frontFacingPolygons
            .filter((p) => p.view_type === currentViewType)
            .forEach((polygon) => {
              const coords = polygon.map_coordinates as { type?: string; points?: number[][] } | null;
              if (!coords?.points || coords.points.length < 3) return;
              map.set(polygon.unit_number, {
                points: coords.points,
                color: 'rgba(59, 130, 246, 0.2)',
              });
            });
          return map;
        })()
      : existingPolygons;

  const viewBox = imageDimensions
    ? `0 0 ${imageDimensions.width} ${imageDimensions.height}`
    : '0 0 1728 864';

  return (
    <div className="container mx-auto p-4 space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">Front Facing Tracer</h1>
          <p className="text-sm text-muted-foreground">
            Trace front-facing building render polygons for each unit
          </p>
        </div>
      </div>

      {/* Exterior Render Upload */}
      <ExteriorRenderUpload 
        block={selectedBlock} 
        onImageUpdated={handleRenderUpdated}
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-4 order-2 lg:order-1">
          {/* Block Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Block Selection</CardTitle>
              <CardDescription className="text-xs">Select building block</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                <Label htmlFor="block-select">Block</Label>
                <Select
                  value={selectedBlock}
                  onValueChange={(v) => {
                    setSelectedBlock(v as 'A' | 'B');
                    setSelectedUnit(null);
                    setSelectedUnitId(null);
                    setSelectedPolygon(null);
                    setCurrentPolygon([]);
                    setHistory([]);
                    setHistoryIndex(-1);
                  }}
                >
                  <SelectTrigger id="block-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">Block A</SelectItem>
                    <SelectItem value="B">Block B</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Unit Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Unit Selection</CardTitle>
              <CardDescription className="text-xs">
                Choose a unit to trace ({blockUnits.length} units in Block {selectedBlock})
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                <Label htmlFor="unit-select">Unit</Label>
                <Select
                  value={selectedUnit || ''}
                  onValueChange={(v) => {
                    const unit = blockUnits.find(u => u.unit_number === v);
                    if (unit) {
                      setSelectedUnit(unit.unit_number);
                      setSelectedUnitId(unit.id);
                    } else {
                      setSelectedUnit(null);
                      setSelectedUnitId(null);
                    }
                  }}
                >
                  <SelectTrigger id="unit-select">
                    <SelectValue placeholder="Select a unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {blockUnits.map((unit) => (
                      <SelectItem key={unit.id} value={unit.unit_number}>
                        {unit.unit_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Front Facing Polygons List */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Front Facing Polygons</CardTitle>
              <CardDescription className="text-xs">
                {(() => {
                  const list = blockBMultiImage
                    ? frontFacingPolygons.filter((p) => p.view_type === currentViewType)
                    : frontFacingPolygons;
                  return `${list.length} polygon${list.length !== 1 ? 's' : ''} for ${blockBMultiImage ? selectedView : 'block'}`;
                })()}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-1 max-h-[300px] sm:max-h-[400px] overflow-y-auto -mr-2 pr-2">
                {(() => {
                  const list = blockBMultiImage
                    ? frontFacingPolygons.filter((p) => p.view_type === currentViewType)
                    : frontFacingPolygons;
                  return list.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No front-facing polygons for this view yet
                  </p>
                ) : (
                  list
                    .sort((a, b) => {
                      const numA = parseInt(a.unit_number.split('-')[1]);
                      const numB = parseInt(b.unit_number.split('-')[1]);
                      return numA - numB;
                    })
                    .map((polygon) => {
                      const coords = polygon.map_coordinates as any;
                      const pointCount = coords?.points?.length || 0;
                      const isSelected = selectedPolygon?.id === polygon.id;
                      
                      return (
                        <div
                          key={polygon.id}
                          className={cn(
                            'flex items-center justify-between p-2 rounded-md hover:bg-muted cursor-pointer transition-colors',
                            isSelected && 'bg-muted border border-primary'
                          )}
                          onClick={() => {
                            setSelectedPolygon(polygon);
                            const unit = allUnits.find(u => u.id === polygon.unit_id);
                            if (unit) {
                              setSelectedUnit(unit.unit_number);
                              setSelectedUnitId(unit.id);
                            }
                          }}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                            <span className="text-sm font-medium truncate">
                              {polygon.unit_number}
                            </span>
                            {pointCount > 0 && (
                              <Badge variant="outline" className="text-xs px-1.5 py-0 shrink-0">
                                {pointCount} pts
                              </Badge>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 shrink-0"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (confirm(`Delete polygon for ${polygon.unit_number}?`)) {
                                const result = await deleteFrontFacingPolygon(polygon.id);
                                if (result.success) {
                                  await loadFrontFacingPolygons();
                                  if (selectedPolygon?.id === polygon.id) {
                                    setSelectedPolygon(null);
                                    setCurrentPolygon([]);
                                    setHistory([]);
                                    setHistoryIndex(-1);
                                  }
                                } else {
                                  toast({
                                    title: 'Delete Failed',
                                    description: result.error || 'Unknown error',
                                    variant: 'destructive',
                                  });
                                }
                              }
                            }}
                            title="Delete polygon"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      );
                    })
                )
                })()}
              </div>
            </CardContent>
          </Card>

          <CoordinatePanel onImportComplete={loadFrontFacingPolygons} />
        </div>

        {/* Main Canvas Area */}
        <div className="lg:col-span-3 space-y-4 order-1 lg:order-2">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-lg sm:text-xl truncate">
                    {selectedUnit
                      ? `Tracing: ${selectedUnit} (Block ${selectedBlock})`
                      : 'Select a unit to start'}
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    {currentPolygon.length > 0
                      ? `${currentPolygon.length} point${currentPolygon.length !== 1 ? 's' : ''} - Click to add more`
                      : 'Click on the front-facing render to add polygon points'}
                  </CardDescription>
                </div>
                <Button
                  onClick={handleSave}
                  disabled={currentPolygon.length < 3 || saving || !selectedUnitId}
                  className="w-full sm:w-auto shrink-0"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">Save Polygon</span>
                      <span className="sm:hidden">Save</span>
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {selectedBlock === 'B' && blockBMultiImage && (
                <Tabs value={selectedView} onValueChange={(v) => setSelectedView(v as BlockBViewId)} className="mb-4">
                  <TabsList className="grid w-full max-w-md grid-cols-3">
                    <TabsTrigger value="southside">Southside</TabsTrigger>
                    <TabsTrigger value="center">Center</TabsTrigger>
                    <TabsTrigger value="northside">Northside</TabsTrigger>
                  </TabsList>
                </Tabs>
              )}

              <TracingControls
                onZoomIn={() => setZoom(prev => Math.min(prev + 0.25, 5))}
                onZoomOut={() => setZoom(prev => Math.max(prev - 0.25, 0.5))}
                onZoomReset={() => {
                  setZoom(1);
                  setPan({ x: 0, y: 0 });
                }}
                onUndo={handleUndo}
                onRedo={handleRedo}
                onClear={handleClear}
                canUndo={historyIndex > 0}
                canRedo={historyIndex < history.length - 1}
                hasPoints={currentPolygon.length > 0}
                zoom={zoom}
                showGrid={showGrid}
                onToggleGrid={() => setShowGrid(!showGrid)}
                className="mb-4"
              />

              <div className="border rounded-lg overflow-hidden" style={{ height: 'min(600px, calc(100vh - 400px))', minHeight: '400px' }}>
                {imageLoadError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
                    <div className="text-center p-4">
                      <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-destructive" />
                      <p className="text-sm font-medium">Failed to load image</p>
                      <p className="text-xs text-muted-foreground mt-1">Please check the image URL</p>
                    </div>
                  </div>
                )}
                <MapCanvas
                  imageUrl={imageUrl}
                  viewBox={viewBox}
                  currentPolygon={currentPolygon}
                  existingPolygons={existingPolygonsForCanvas}
                  selectedUnit={selectedUnit}
                  onPointAdd={handlePointAdd}
                  onPointDelete={handlePointDelete}
                  onPointMove={handlePointMove}
                  onPolygonMove={handlePolygonMove}
                  onPolygonMoveStart={handlePolygonMoveStart}
                  zoom={zoom}
                  pan={pan}
                  onPanChange={setPan}
                  hiddenPolygons={hiddenPolygons}
                  currentPolygonKey={(() => {
                    const key = selectedUnit || null;
                    console.log('AdminFrontFacingTracer: Passing currentPolygonKey to MapCanvas', {
                      selectedUnit,
                      selectedUnitId,
                      currentPolygonKey: key,
                      currentPolygonLength: currentPolygon.length,
                      hiddenPolygons: Array.from(hiddenPolygons),
                    });
                    return key;
                  })()}
                  imageWidth={imageDimensions?.width}
                  imageHeight={imageDimensions?.height}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

